import { supabase } from '@/lib/supabase/client';
import type {
  ExistingAccessAction,
  StoreReviewPacket,
  UserAccessReviewCase,
  UserAccessReviewSubmission,
  UserAccessRole,
} from '@/lib/customer-ops/user-access-review';

const USER_ACCESS_REVIEW_SESSION_FUNCTION = 'user-access-review-session';
const USER_ACCESS_REVIEW_ADMIN_API = '/api/user-access-review-admin';

export interface OpenUserAccessReviewSessionResponse {
  ok: boolean;
  packet?: StoreReviewPacket;
  status?: string;
  draft_revision?: number;
  error?: string;
  message?: string;
}

export interface SubmitUserAccessReviewResponse {
  ok: boolean;
  duplicate?: boolean;
  status?: string;
  review_case_id?: string;
  submitted_at?: string;
  error?: string;
  message?: string;
}

export interface SaveUserAccessReviewDraftResponse {
  ok: boolean;
  duplicate?: boolean;
  status?: string;
  review_case_id?: string;
  started_at?: string;
  draft_saved_at?: string;
  draft_revision?: number;
  error?: string;
  message?: string;
}

export interface DelegateUserAccessReviewResponse {
  ok: boolean;
  duplicate?: boolean;
  status?: string;
  review_case_id?: string;
  delegated_at?: string | null;
  delegated_to?: {
    name: string;
    email: string;
  };
  delivery_id?: string | null;
  error?: string;
  message?: string;
}

export interface SubmitUserAccessReviewInput {
  token: string;
  packet: StoreReviewPacket;
}

export interface SaveUserAccessReviewDraftInput extends SubmitUserAccessReviewInput {
  draftRevision: number;
}

export async function saveUserAccessReviewDraft({
  token,
  packet,
  draftRevision,
}: SaveUserAccessReviewDraftInput): Promise<SaveUserAccessReviewDraftResponse> {
  return invokeUserAccessReviewSession<SaveUserAccessReviewDraftResponse>({
    action: 'save_draft',
    token,
    draft_revision: draftRevision,
    draft: {
      accountName: packet.accountName,
      storeName: packet.storeName,
      approver: packet.approver,
      notes: packet.notes,
      existingUsers: packet.existingUsers.map((user) => ({
        id: user.id,
        action: user.action,
        role: user.role,
        accountName: user.accountName,
        storeLocation: user.storeLocation,
      })),
      newUsers: packet.newUsers.map((user) => ({
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        accountName: user.accountName,
        storeLocation: user.storeLocation,
      })),
    },
  });
}

type AdminAction =
  | 'list_cases'
  | 'get_submission'
  | 'get_review_link'
  | 'send_request'
  | 'send_reminder'
  | 'mark_sent'
  | 'log_reminder'
  | 'mark_blocked'
  | 'mark_completed';

interface UserAccessReviewAdminListResponse {
  ok: boolean;
  cases?: UserAccessReviewCase[];
  error?: {
    code?: string;
    message?: string;
  };
}

interface UserAccessReviewAdminUpdateResponse {
  ok: boolean;
  case?: UserAccessReviewCase;
  cases?: UserAccessReviewCase[];
  error?: {
    code?: string;
    message?: string;
  };
}

interface UserAccessReviewAdminLinkResponse {
  ok: boolean;
  reviewCaseId?: string;
  reviewUrl?: string;
  error?: {
    code?: string;
    message?: string;
  };
}

interface UserAccessReviewAdminSubmissionResponse {
  ok: boolean;
  submission?: UserAccessReviewSubmission;
  error?: {
    code?: string;
    message?: string;
  };
}

export interface UserAccessReviewAdminScope {
  environment: 'production' | 'test';
  testRunId?: string;
}

async function invokeUserAccessReviewSession<T>(body: object): Promise<T> {
  const { data, error } = await supabase.functions.invoke(USER_ACCESS_REVIEW_SESSION_FUNCTION, {
    body,
  });

  if (error) {
    const response = error.context;
    if (response) {
      let serverMessage: string | null = null;
      try {
        const payload = await response.json();
        serverMessage = payload?.message || payload?.error || null;
      } catch {
        // Fall through to the Supabase function error message.
      }
      if (serverMessage) throw new Error(serverMessage);
    }
    throw new Error(error.message || 'User access review request failed.');
  }

  return data as T;
}

export async function openUserAccessReviewSession(
  token: string
): Promise<OpenUserAccessReviewSessionResponse> {
  return invokeUserAccessReviewSession<OpenUserAccessReviewSessionResponse>({
    action: 'open_session',
    token,
  });
}

export async function submitUserAccessReview({
  token,
  packet,
}: SubmitUserAccessReviewInput): Promise<SubmitUserAccessReviewResponse> {
  return invokeUserAccessReviewSession<SubmitUserAccessReviewResponse>({
    action: 'submit_review',
    token,
    submission: {
      approver: packet.approver,
      notes: packet.notes,
      existingUsers: packet.existingUsers.map((user) => ({
        id: user.id,
        action: user.action as Exclude<ExistingAccessAction, 'unset'>,
        role: user.action === 'migrate' ? user.role : null,
        accountName: user.action === 'migrate' ? user.accountName : null,
        storeLocation: user.action === 'migrate' ? user.storeLocation : null,
      })),
      newUsers: packet.newUsers.map((user) => ({
        name: user.name,
        email: user.email,
        role: user.role as UserAccessRole,
        accountName: user.accountName,
        storeLocation: user.storeLocation,
      })),
    },
  });
}

export async function delegateUserAccessReview({
  token,
  name,
  email,
}: {
  token: string;
  name: string;
  email: string;
}): Promise<DelegateUserAccessReviewResponse> {
  return invokeUserAccessReviewSession<DelegateUserAccessReviewResponse>({
    action: 'delegate_review',
    token,
    delegate: { name, email },
  });
}

export async function listUserAccessReviewCases(
  scope: UserAccessReviewAdminScope = { environment: 'production' }
): Promise<UserAccessReviewCase[]> {
  const response = await invokeUserAccessReviewAdmin<UserAccessReviewAdminListResponse>({
    action: 'list_cases',
    ...scope,
  });
  return response.cases ?? [];
}

export async function updateUserAccessReviewCase(
  action: Exclude<AdminAction, 'list_cases' | 'get_submission' | 'get_review_link'>,
  reviewCaseId: string,
  scope: UserAccessReviewAdminScope,
  extra: Record<string, unknown> = {}
): Promise<UserAccessReviewCase[]> {
  const response = await invokeUserAccessReviewAdmin<UserAccessReviewAdminUpdateResponse>({
    action,
    reviewCaseId,
    ...scope,
    ...extra,
  });
  const updatedCases = response.cases?.length ? response.cases : response.case ? [response.case] : [];
  if (updatedCases.length === 0) {
    throw new Error('User access review case was not returned.');
  }
  return updatedCases;
}

export async function getUserAccessReviewSubmission(
  reviewCaseId: string,
  scope: UserAccessReviewAdminScope
): Promise<UserAccessReviewSubmission> {
  const response = await invokeUserAccessReviewAdmin<UserAccessReviewAdminSubmissionResponse>({
    action: 'get_submission',
    reviewCaseId,
    ...scope,
  });
  if (!response.submission) throw new Error('Submitted review details were not returned.');
  return response.submission;
}

export async function getUserAccessReviewLink(
  reviewCaseId: string,
  scope: UserAccessReviewAdminScope
): Promise<string> {
  const response = await invokeUserAccessReviewAdmin<UserAccessReviewAdminLinkResponse>({
    action: 'get_review_link',
    reviewCaseId,
    ...scope,
  });
  if (!response.reviewUrl) throw new Error('Review link was not returned.');
  return response.reviewUrl;
}

async function invokeUserAccessReviewAdmin<T>(body: object): Promise<T> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session?.access_token) {
    throw new Error('Sign in to load user access reviews.');
  }

  const response = await fetch(USER_ACCESS_REVIEW_ADMIN_API, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${sessionData.session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  let payload: UserAccessReviewAdminListResponse | UserAccessReviewAdminUpdateResponse | null = null;
  try {
    payload = await response.json();
  } catch {
    // Fall through to the response-level error.
  }

  if (!response.ok || !payload?.ok) {
    throw new Error(
      payload?.error?.message ||
        `User access review admin request failed with status ${response.status}.`
    );
  }

  return payload as T;
}
